import React from "react";
import { Check, Monitor, Moon, Sun } from "lucide-react";
import { AN_COLORS, AN_DARK, AN_FONT, AN_LIGHT } from "../../design-system/tokens";
import { useAnTheme } from "../../theme/AnThemeProvider";
import { SettingsPage, SettingsSection } from "./settingsUi";

// Settings → Appearance: Light (the default), Dark, or System. Saved in this
// browser, for every MFA mode.

const OPTIONS = [
  { id: "light", label: "Light", Icon: Sun, hint: "The default." },
  { id: "dark", label: "Dark", Icon: Moon, hint: "Easier on the eyes in a dark room." },
  { id: "system", label: "System", Icon: Monitor, hint: "Follows your operating system's setting." },
];

// A miniature of the console: sidebar, header and two cards.
function Preview({ palette, half }) {
  const pane = (p) => (
    <div style={{ display: "flex", height: "100%", background: p.bgWarm }}>
      <div style={{ width: "26%", background: p.surface, borderRight: `1px solid ${p.line}`, padding: "10px 7px", display: "grid", gap: 5, alignContent: "start" }}>
        <span style={{ height: 6, borderRadius: 3, background: p.primary, width: "70%" }} />
        <span style={{ height: 5, borderRadius: 3, background: p.line }} />
        <span style={{ height: 5, borderRadius: 3, background: p.line, width: "80%" }} />
        <span style={{ height: 5, borderRadius: 3, background: p.line, width: "60%" }} />
      </div>
      <div style={{ flex: 1, padding: 9, display: "grid", gap: 7, alignContent: "start" }}>
        <span style={{ height: 7, borderRadius: 3, background: p.ink, width: "45%", opacity: 0.85 }} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {[0, 1].map((i) => (
            <div key={i} style={{ height: 34, borderRadius: 5, background: p.surface, border: `1px solid ${p.line}`, padding: 5, display: "grid", gap: 4, alignContent: "start" }}>
              <span style={{ height: 4, borderRadius: 2, background: p.muted, width: "60%", opacity: 0.7 }} />
              <span style={{ height: 6, borderRadius: 2, background: i ? p.success : p.primary, width: "35%" }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  if (!half) return pane(palette);
  // System: light on the left, dark on the right.
  return (
    <div style={{ position: "relative", height: "100%" }}>
      <div style={{ position: "absolute", inset: 0 }}>{pane(AN_LIGHT)}</div>
      <div style={{ position: "absolute", inset: 0, clipPath: "polygon(55% 0, 100% 0, 100% 100%, 45% 100%)" }}>{pane(AN_DARK)}</div>
    </div>
  );
}

export default function Appearance() {
  const { preference, mode, setPreference } = useAnTheme();

  return (
    <SettingsPage title="Appearance" description="Choose how the console looks in this browser. It applies to every MFA mode.">
      <SettingsSection bare title="Theme" description={preference === "system" ? `Following your system: ${mode === "dark" ? "Dark" : "Light"} right now.` : undefined}>
        <div role="radiogroup" aria-label="Theme" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 14 }}>
          {OPTIONS.map(({ id, label, Icon, hint }) => {
            const on = preference === id;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setPreference(id)}
                className="an-theme-option"
                style={{
                  textAlign: "left",
                  padding: 0,
                  borderRadius: 12,
                  overflow: "hidden",
                  cursor: "pointer",
                  background: AN_COLORS.surface,
                  border: `1.5px solid ${on ? AN_COLORS.primary : AN_COLORS.line}`,
                  boxShadow: on ? `0 0 0 3px ${AN_COLORS.primaryL}` : "none",
                  fontFamily: AN_FONT,
                  transition: "border-color .15s, box-shadow .15s",
                }}
              >
                <div style={{ height: 112, borderBottom: `1px solid ${AN_COLORS.line}` }}>
                  <Preview palette={id === "dark" ? AN_DARK : AN_LIGHT} half={id === "system"} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px" }}>
                  <Icon size={16} color={on ? AN_COLORS.primary : AN_COLORS.ink2} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: AN_COLORS.ink }}>{label}</span>
                    <span style={{ display: "block", fontSize: 12, color: AN_COLORS.muted, marginTop: 1 }}>{hint}</span>
                  </span>
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: on ? AN_COLORS.primary : "transparent",
                      border: on ? "none" : `1.5px solid ${AN_COLORS.line}`,
                    }}
                  >
                    {on && <Check size={12} color="#fff" strokeWidth={3} />}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </SettingsSection>
    </SettingsPage>
  );
}
